import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import LoadingSpinner from '@/components/LoadingSpinner';

// Admin page components
import EnhancedSystemAnalytics from '@/components/admin/EnhancedSystemAnalytics';
import BusinessIntelligence from '@/components/admin/BusinessIntelligence';
import { CreditManagement } from '@/components/admin/CreditManagement';
import BillingManagement from '@/components/admin/BillingManagement';
import ReferralManagement from '@/components/admin/ReferralManagement';
import PractitionerManagement from '@/components/admin/PractitionerManagement';
import UserManagement from '@/components/admin/UserManagement';
import SystemHealth from '@/components/admin/SystemHealth';
import ContentManagement from '@/components/admin/ContentManagement';
import AdminShortsGenerator from '@/components/admin/AdminShortsGenerator';
import SampleSessionUploader from '@/components/admin/SampleSessionUploader';
import AdminErrorsPage from '@/pages/AdminErrorsPage';
import { CommunitySessionsReview } from '@/components/admin/CommunitySessionsReview';
import { AdminSharedSessionsPage } from '@/pages/AdminSharedSessionsPage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Upload } from 'lucide-react';

const AdminSettingsPage = () => {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">System Settings</h1>
        <p className="text-muted-foreground">
          Configure system-wide settings and maintenance operations
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>System Settings</CardTitle>
            <p className="text-sm text-muted-foreground">
              Configure system-wide settings and maintenance operations.
            </p>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              Advanced system configuration coming soon...
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Sample Session Management
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Upload curated sample sessions for each category.
            </p>
          </CardHeader>
          <CardContent>
            <SampleSessionUploader />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AdminPage: React.FC = () => {
  const { loading, isAdmin } = useUserRole();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/app" replace />;
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />

        <div className="flex-1 flex flex-col">
          <header className="h-14 flex items-center border-b border-border px-6">
            <SidebarTrigger />
          </header>

          <main className="flex-1 p-6">
            <Breadcrumbs />
            <Routes>
              <Route path="/" element={<EnhancedSystemAnalytics />} />
              <Route path="business" element={<BusinessIntelligence />} />
              <Route path="credits" element={<CreditManagement />} />
              <Route path="billing" element={<BillingManagement />} />
              <Route path="referrals" element={<ReferralManagement />} />
              <Route path="practitioners" element={<PractitionerManagement />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="system" element={<SystemHealth />} />
              <Route path="errors" element={<AdminErrorsPage />} />
              <Route path="content" element={<ContentManagement />} />
              <Route path="shorts" element={<AdminShortsGenerator />} />
              <Route path="community" element={<CommunitySessionsReview />} />
              <Route path="shared-sessions" element={<AdminSharedSessionsPage />} />
              <Route path="settings" element={<AdminSettingsPage />} />
            </Routes>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default AdminPage;
